import React, { createRef } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import moment, { JUnitOfTime } from 'moment-jalaali';
import { convertDigitToProperFormat, isANumber } from 'utils/number';
import { endOfDate, monthToTimestamp, startOfDate } from 'utils/date';
import { ITasks, IUsers, RootState } from 'store/store';
import { IOption } from 'components/Select/Select';
import { NumberOrNull, StringOrNull } from 'store/entity/types';
import messages from './Messages';
import { display } from 'components/ErrorView/ErrorDialog';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import TaskReportModal from 'components/Report/TaskReportModal';
import DateSelector from 'components/Report/DateSelector';
import ReportChart from 'components/Report/ReportChart';
import ReportInfoBoxes from 'components/Report/ReportInfoBoxes';
import Icon from 'components/Icon';
import Select from 'components/Select';
import notify from 'components/Notify';
import { setClipboard } from 'containers/Tasks/TasksManagement/TaskUtils';
import { ITaskReportInfo } from 'store/providers/providerTypes';
import { isEqual, upperFirst } from 'lodash';
import ActionLink from '../../ActionLink/ActionLink';
import { getPureTextFromHtml } from 'store/tasks/taskManipulate';
import { roundToDynamicFloating } from '../../Chart/analyseUtils';
import { appDispatch } from 'store/store';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  ITemplateReportMark,
  ITemplateReportValues,
} from 'store/templates/templatesSlice';
import { getTaskReport, getTaskReportFn } from 'store/tasks/tasksSlice';
import { isCompanyAdmin } from 'store/companies/companiesUtils';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ICompany, TProjects } from 'store/companies/companiesTypes';
import {
  Message,
  ReportBody,
  ReportConfigContainer,
  ReportHeader,
  ReportRangeDate,
  ReportSection,
} from './styles';
import cloneDeep from 'lodash/cloneDeep';
import { smartNumericCompare } from '../../../utils/string';
import { ILocal } from '../../Chart/BaseDatePicker';
import i18n, { getCalendarLocal, getLangBaseOnLocal } from '../../../i18n';
import { getLanguageBaseOnTZ } from '../../../utils/LanguageLocalStorage';
import UserMixpanel from '../../../utils/mixpanel/MixpanelClass';
import ExportExcell from '../../../containers/Tasks/CompanyManagement/PDPClass/ExportExcell';
import { black } from 'utils/style/colors';

moment.loadPersian({ dialect: 'persian-modern' });

interface IStateToProps {
  company: ICompany;
  projects: TProjects;
  tasks: ITasks['data'];
  users: IUsers['map'];
  thisUser: IThisUserState;
  isAdmin: boolean;
}

interface IDispatchToProps {
  getReport: (
    companyId: number,
    templateReport: ITemplateReportMark,
    startDate: number,
    toDate: number,
    projectId: NumberOrNull,
    parentId: NumberOrNull,
    templateId: number,
    assigneeId: NumberOrNull,
    splitMonths: number,
    splitField: StringOrNull,
    fieldValues: { [key: string]: string }
  ) => ReturnType<typeof getTaskReportFn>;
  requireUsers: (userIds: number[]) => ReturnType<typeof requireSeveralUserFn>;
}

interface IOwnProps {
  parentId?: number;
  companyId: number;
  templateId?: number;
  defaultConfig?: ITaskReportInfo;
  readonly?: boolean;
  inSurvey?: boolean;
  height?: number;
  baseDate?: Date;
  compactView: boolean;
  activeProjectId?: number;
  handleChangeProjectId: (projectId?: number) => void;
  fieldValues: { [key: string]: string };
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  assignee: IOption | null;
  split: IOption;
  chartData: ITemplateReportValues[];
  chartView: string;
  loading: boolean;
  preparingReport: boolean;
  selectedItem?: {
    searchParams: any;
    endMonthIndex: number;
    startMonthIndex: number;
  };
  showTaskModal: boolean;
  splitMonths: number;
  timeLength: number;
  startDate: number;
  tableData: ITemplateReportValues;
  toDate: number;
  showRelative: boolean;
  reportShowRelative: boolean;
  chartContainerWidth: number;
}

function getYear(item: ITemplateReportValues) {
  const y = Math.floor(item.endMonthIndex / 12);
  if (y < 10) return convertDigitToProperFormat('0' + y);
  return convertDigitToProperFormat(y);
}

export const getChartData = (
  split: any,
  projects: TProjects,
  tasks: ITasks['data'],
  templateId: number,
  splitField: StringOrNull,
  splitMonths: number,
  showRelative: boolean,
  local: ILocal,
  projectId?: number | null
): ITemplateReportValues[] => {
  if (split) {
    const templateReportOptions = tasks[templateId].templateReportOptions;
    const allFieldValues = new Set<string>();
    if (
      splitField !== '__time' &&
      splitField !== '__project' &&
      !splitField?.startsWith('__ancestor/')
    ) {
      Object.values(
        tasks[templateId].fields.find((it: any) => it.name === splitField)
          ?.options || {}
      )?.forEach((v: string) => {
        allFieldValues.add(v);
      });
      Object.values(projects || {}).forEach((p) => {
        if (!projectId || p.id === projectId)
          p.templates
            .find((it: any) => {
              return it.taskId === templateId;
            })
            ?.overrides?.fieldOptions[splitField!]?.forEach((v: string) => {
              allFieldValues.add(v);
            });
      });
    }
    const result = split.map((item: ITemplateReportValues) => {
      const totalMain =
        (item.completedTaskCountMain ? item.completedTaskCountMain : 0) +
        (item.inProgressTaskCountMain ? item.inProgressTaskCountMain : 0) +
        (item.notStartedTaskCountMain ? item.notStartedTaskCountMain : 0);
      const totalSecondary =
        (item.completedTaskCountSecondary
          ? item.completedTaskCountSecondary
          : 0) +
        (item.inProgressTaskCountSecondary
          ? item.inProgressTaskCountSecondary
          : 0) +
        (item.notStartedTaskCountSecondary
          ? item.notStartedTaskCountSecondary
          : 0);
      const totalVerifier =
        (item.completedTaskCountVerifier
          ? item.completedTaskCountVerifier
          : 0) +
        (item.rejectedTaskCountVerifier ? item.rejectedTaskCountVerifier : 0) +
        (item.notStartedTaskCountVerifier
          ? item.notStartedTaskCountVerifier
          : 0);
      const totalAssignment =
        (item.completedAssignmentCount ? item.completedAssignmentCount : 0) +
        (item.inProgressAssignmentCount ? item.inProgressAssignmentCount : 0) +
        (item.notStartedAssignmentCount ? item.notStartedAssignmentCount : 0);
      const newItem: any = item;
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalMain,
        'completedTaskCountMain',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalMain,
        'inProgressTaskCountMain',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalMain,
        'notStartedTaskCountMain',
        showRelative,
        local
      );
      computeAvgValue(
        item,
        newItem,
        templateReportOptions,
        'averageProgressMain'
      );

      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalSecondary,
        'completedTaskCountSecondary',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalSecondary,
        'inProgressTaskCountSecondary',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalSecondary,
        'notStartedTaskCountSecondary',
        showRelative,
        local
      );
      computeAvgValue(
        item,
        newItem,
        templateReportOptions,
        'averageProgressSecondary'
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalVerifier,
        'completedTaskCountVerifier',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalVerifier,
        'rejectedTaskCountVerifier',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalVerifier,
        'notStartedTaskCountVerifier',
        showRelative,
        local
      );

      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalAssignment,
        'completedAssignmentCount',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalAssignment,
        'inProgressAssignmentCount',
        showRelative,
        local
      );
      computeItemValue(
        item,
        newItem,
        templateReportOptions,
        totalAssignment,
        'notStartedAssignmentCount',
        showRelative,
        local
      );
      computeAvgValue(
        item,
        newItem,
        templateReportOptions,
        'averageAssignmentProgress'
      );

      if (splitField === '__time') {
        if (splitMonths === 12) {
          newItem.title = getYear(item);
        } else if (splitMonths === 3) {
          const monthNumber: number = (item.endMonthIndex % 12) + 1;
          if (monthNumber <= 3) {
            newItem.title =
              i18n.t('firstSeason', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          } else if (monthNumber > 3 && monthNumber <= 6) {
            newItem.title =
              i18n.t('secondSeason', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          } else if (monthNumber > 6 && monthNumber <= 9) {
            newItem.title =
              i18n.t('thirdSeason', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          } else if (monthNumber > 9 && monthNumber <= 12) {
            newItem.title =
              i18n.t('forthSeason', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          }
        } else if (splitMonths === 6) {
          const monthNumber: number = (item.endMonthIndex % 12) + 1;
          if (monthNumber <= 6) {
            newItem.title =
              i18n.t('firstHalfYear', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          } else {
            newItem.title =
              i18n.t('secondHalfYear', {
                ns: 'taskManager',
                lng: getLangBaseOnLocal(local),
              }) +
              ' ' +
              getYear(item);
          }
        } else {
          const mFormat = local === 'fa_IR@calendar=persian' ? 'jM' : 'M';
          const fullMFormat =
            local === 'fa_IR@calendar=persian' ? 'jMMMM' : 'MMMM';
          const startMonth: string = (
            local === 'fa_IR@calendar=persian'
              ? moment((item.startMonthIndex % 12) + 1, mFormat)
              : moment((item.startMonthIndex % 12) + 1, mFormat).locale('en')
          ).format(fullMFormat);
          const endMonth: string = (
            local === 'fa_IR@calendar=persian'
              ? moment((item.endMonthIndex % 12) + 1, mFormat)
              : moment((item.endMonthIndex % 12) + 1, mFormat).locale('en')
          ).format(fullMFormat);
          newItem.title =
            startMonth === endMonth
              ? startMonth + ' ' + getYear(item)
              : `${startMonth}-${endMonth} ` + getYear(item);
        }
      } else {
        newItem.title = getPureTextFromHtml(item.title || item.fieldValue);
        allFieldValues.delete(newItem.title);
      }
      return newItem;
    });
    allFieldValues.forEach((v) => {
      result.push({ title: v, fieldValue: v });
    });
    if (splitField !== '__time') {
      result.sort((a: any, b: any) => smartNumericCompare(a.title, b.title));
    }
    return result;
  }
  return [];
};

function computeAvgValue(
  item: ITemplateReportValues,
  newItem: any,
  templateReportOptions: any,
  key: string
) {
  if (item[key] !== undefined) {
    newItem[templateReportOptions[key] ? templateReportOptions[key] : key] =
      roundToDynamicFloating(item[key]);
  }
}

function computeItemValue(
  item: ITemplateReportValues,
  newItem: any,
  templateReportOptions: any,
  totalMain: number,
  key: string,
  showRelative: boolean,
  local: ILocal = getCalendarLocal()
) {
  const keySuffix = showRelative
    ? ` (${i18n.t('percentage', { ns: 'taskManager', lng: getLangBaseOnLocal(local) })})`
    : '';
  if (item[key] !== undefined) {
    newItem[
      templateReportOptions[key]
        ? `${templateReportOptions[key]}${keySuffix}`
        : key
    ] =
      totalMain > 0
        ? roundToDynamicFloating(
            (item[key] / (showRelative ? totalMain : 1)) *
              (showRelative ? 100 : 1)
          )
        : 0;
  }
}

function getParents(
  company: ICompany,
  templateId: number,
  current: Map<number, string>,
  tasks: ITasks['data']
) {
  Object.values(company.templates || {}).forEach((template) => {
    const parentId = template.taskId;
    if (template.children?.includes(templateId) && !current.has(parentId)) {
      current.set(parentId, tasks[parentId].taskName);
      getParents(company, parentId, current, tasks);
    }
  });
}

class TaskReport extends React.Component<IComponentProps, IComponentStates> {
  state: IComponentStates = {
    loading: true,
    preparingReport: false,
    chartView: 'main',
    chartData: [],
    showTaskModal: false,
    assignee: null as any,
    showRelative: true,
    timeLength: 1,
    startDate:
      ((this.props.company.local || getCalendarLocal()) ===
      'fa_IR@calendar=persian'
        ? moment().startOf('jMonth')
        : moment().locale('en').startOf('month')
      )
        .set({ hour: 0, minute: 0, seconds: 0 })
        .unix() * 1000,
    toDate:
      ((this.props.company.local || getCalendarLocal()) ===
      'fa_IR@calendar=persian'
        ? moment().endOf('jMonth')
        : moment().locale('en').endOf('month')
      )
        .set({ hour: 23, minute: 59, seconds: 59, millisecond: 999 })
        .unix() * 1000,
    splitMonths: 1,
    selectedItem: {
      startMonthIndex: 0,
      endMonthIndex: 0,
      searchParams: undefined,
    },
    tableData: { startMonthIndex: 0, endMonthIndex: 0 },
    split: {
      label: i18n.t('time', { ns: 'taskManager' }),
      value: '__time',
    },
    reportShowRelative: true,
    chartContainerWidth: 0,
  };

  copyTextRef = createRef<HTMLInputElement>();
  chartContainerRef = createRef<HTMLDivElement>();

  static getDerivedStateFromProps(nextProps: IComponentProps) {
    if (!nextProps.templateId) {
      return {
        chartData: [],
        tableData: {
          startMonthIndex: 0,
          endMonthIndex: 0,
        },
      };
    }
    return {};
  }

  componentDidMount() {
    const companyMembers = Object.keys(this.props.company.members).map((it) =>
      Number(it)
    );
    this.props
      .requireUsers(companyMembers)
      .then(() => this.setState({ loading: false }))
      .catch((err) => display(err));
    this.setDefaultConfig();
  }

  setDefaultConfig = () => {
    if (this.props.defaultConfig) {
      this.setState({
        timeLength: this.props.defaultConfig.timeLength,
        assignee: this.props.defaultConfig.assigneeId
          ? { value: this.props.defaultConfig.assigneeId, label: '' }
          : null,
        splitMonths: this.props.defaultConfig.splitMonths,
        split: { value: this.props.defaultConfig.splitField, label: '' },
        chartView: this.props.defaultConfig.visiblePart,
        showRelative: this.props.defaultConfig.showRelative,
      });

      if (this.props.baseDate) {
        const timeLength = this.props.defaultConfig.timeLength;
        const selectedDateType =
          this.props.defaultConfig.splitMonths === 1
            ? 'month'
            : this.props.defaultConfig.splitMonths === 3
              ? 'season'
              : this.props.defaultConfig.splitMonths === 6
                ? 'twoSeason'
                : this.props.defaultConfig.splitMonths === 12
                  ? 'year'
                  : 'month';

        const local = (this.props.company.local ||
          getCalendarLocal()) as ILocal;
        const monthFormat =
          local === 'fa_IR@calendar=persian' ? 'jMonth' : 'month';
        const getBaseDate = this.props.baseDate || Date();
        const toDate = endOfDate(
          getBaseDate,
          selectedDateType,
          true,
          getLangBaseOnLocal(local)
        );

        let subtractedDate =
          local === 'fa_IR@calendar=persian'
            ? moment(toDate)
            : moment(toDate).locale('en');

        if (selectedDateType === 'month' || selectedDateType === 'year') {
          const scale =
            local === 'fa_IR@calendar=persian'
              ? `j${upperFirst(selectedDateType)}`
              : `${upperFirst(selectedDateType)}`;
          subtractedDate = subtractedDate.subtract(
            timeLength - 1,
            scale as moment.unitOfTime.DurationConstructor
          );
        } else if (selectedDateType === 'season') {
          subtractedDate = subtractedDate.subtract(
            (timeLength - 1) * 3,
            monthFormat as moment.unitOfTime.DurationConstructor
          );
        } else if (selectedDateType === 'twoSeason') {
          subtractedDate = subtractedDate.subtract(
            (timeLength - 1) * 6,
            monthFormat as moment.unitOfTime.DurationConstructor
          );
        }

        const startDate = startOfDate(
          subtractedDate.toDate(),
          selectedDateType,
          true,
          getLangBaseOnLocal(local)
        );
        this.handleChangeDate(
          this.props.defaultConfig.splitMonths,
          startDate,
          toDate,
          timeLength
        );
      }
      if (this.props.readonly) {
        setTimeout(() => {
          this.onPrepareReport();
        }, 100);
      }
    }
  };

  componentDidUpdate(prevProps: IComponentProps, prevState: IComponentStates) {
    const local = (this.props.company.local || getCalendarLocal()) as ILocal;
    const monthFormat = local === 'fa_IR@calendar=persian' ? 'jMonth' : 'month';
    if (!isEqual(prevProps.defaultConfig, this.props.defaultConfig))
      this.setDefaultConfig();
    // to reset chartData on change template and project
    if (
      isANumber(this.props.templateId) &&
      (prevProps.templateId !== this.props.templateId ||
        prevProps.activeProjectId !== this.props.activeProjectId)
    ) {
      // reset when templateId has undefined value
      const resetDate = !this.props.templateId;
      const { toDate, startDate } = this.state;
      this.setState({
        chartData: [],
        startDate: resetDate
          ? (local === 'fa_IR@calendar=persian'
              ? moment()
              : moment().locale('en')
            )
              .endOf(monthFormat as JUnitOfTime)
              .subtract(1, 'month')
              .unix() *
              1000 +
            1000
          : startDate,
        toDate: resetDate
          ? (local === 'fa_IR@calendar=persian'
              ? moment()
              : moment().locale('en')
            )
              .endOf(monthFormat as JUnitOfTime)
              .unix() * 1000
          : toDate,
      });
    }
    if (
      prevState.chartData !== this.state.chartData &&
      this.chartContainerRef.current
    ) {
      this.setState({
        chartContainerWidth: this.chartContainerRef.current.clientWidth,
      });
    }
  }

  onPrepareReport = (): void => {
    const templateId = this.props.templateId;
    const fieldValues = this.props.fieldValues;
    if (!templateId) {
      notify(
        i18n.t('toPrepareTheReportYouShouldSelectAtTemplate', {
          ns: 'taskManager',
        }),
        'warning'
      );
      return;
    }

    this.setState({ preparingReport: true });

    const { toDate, startDate, assignee, splitMonths, split } = this.state;

    const parentId: NumberOrNull = this.props.parentId
      ? this.props.parentId
      : null;

    let templateReportOptions = cloneDeep(
      this.props.tasks[templateId].templateReportOptions
    );
    if (templateReportOptions) {
      delete templateReportOptions.mainProgressTitle;
      delete templateReportOptions.secondaryProgressTitle;
      delete templateReportOptions.verifierTitle;
    } else {
      templateReportOptions = {};
    }

    const reportMark: ITemplateReportMark = Object.keys(
      templateReportOptions
    ).reduce((marks: any, prop: string) => {
      marks[prop] = Boolean(templateReportOptions![prop]);
      return marks;
    }, {});

    const projectId = this.props.activeProjectId
      ? this.props.activeProjectId
      : null;

    const assigneeId = assignee ? (assignee.value as number) : null;

    const splitField = split.value.toString();
    const local = (this.props.company.local || getCalendarLocal()) as ILocal;
    const mx = UserMixpanel.getInstance();

    this.props
      .getReport(
        this.props.companyId,
        reportMark,
        startDate,
        toDate,
        projectId,
        parentId,
        templateId,
        assigneeId,
        splitMonths,
        splitField,
        fieldValues
      )
      .then(({ data }) => {
        mx.sendCreateReportFromTaskTrack(`${data.name}`);
        const showRelative = this.state.showRelative;
        const chartData: ITemplateReportValues[] = getChartData(
          data.split,
          this.props.projects,
          this.props.tasks,
          this.props.templateId!,
          splitField,
          this.state.splitMonths,
          showRelative,
          local as ILocal,
          projectId
        );
        const tableData: ITemplateReportValues = data.total;
        this.setState({
          preparingReport: false,
          chartData,
          tableData,
          reportShowRelative: showRelative,
        });
      })
      .catch((err) => {
        this.setState({ preparingReport: false });
        display(err);
      });
  };

  handleClickOnBar =
    (type: string) =>
    (chartNode: any): void => {
      const { split } = this.state;
      const { startMonthIndex, endMonthIndex, fieldValue } = chartNode.payload;
      const searchParams = this.makeSearchParams(type);
      if (split.value === '__project') {
        searchParams.projectId = Number(fieldValue);
      }
      if (split.value === '__parent') {
        searchParams.parentId = Number(fieldValue);
      }
      if (split.value?.toString()?.startsWith('__ancestor/')) {
        searchParams.ancestorId = Number(fieldValue);
      } else if (split.value !== '__time') {
        searchParams.extraFieldKey = this.state.split.value;
        searchParams.extraFieldValue = fieldValue;
      }
      searchParams.draft = false;
      this.setState({
        selectedItem: { searchParams, endMonthIndex, startMonthIndex },
      });
      this.handleShowTaskModal(true);
    };

  handleClickOnShowAll = (chartData: ITemplateReportValues[]) => () => {
    let endMonthIndex = chartData[0].endMonthIndex;
    let startMonthIndex = chartData[0].startMonthIndex;
    chartData.forEach((data) => {
      if (data.endMonthIndex > endMonthIndex) {
        endMonthIndex = data.endMonthIndex;
      }
      if (data.startMonthIndex < startMonthIndex) {
        startMonthIndex = data.startMonthIndex;
      }
    });
    this.setState({
      selectedItem: { searchParams: 'all', endMonthIndex, startMonthIndex },
    });
    this.handleShowTaskModal(true);
  };

  makeSearchParams = (itemType: string) => {
    const params: any = {};
    switch (itemType) {
      case 'completedTaskCountMain':
        params.mainProgressFrom = 100;
        params.mainProgressTo = 100;
        return params;
      case 'inProgressTaskCountMain':
        params.mainProgressFrom = 0.01;
        params.mainProgressTo = 99.99;
        return params;
      case 'notStartedTaskCountMain':
        params.mainProgressFrom = 0;
        params.mainProgressTo = 0;
        return params;
      case 'completedTaskCountSecondary':
        params.secondaryProgressFrom = 100;
        params.secondaryProgressTo = 100;
        return params;
      case 'inProgressTaskCountSecondary':
        params.secondaryProgressFrom = 0.01;
        params.secondaryProgressTo = 99.99;
        return params;
      case 'notStartedTaskCountSecondary':
        params.secondaryProgressFrom = 0;
        params.secondaryProgressTo = 0;
        return params;
      case 'completedTaskCountVerifier':
        params.verifyStatus = 'complete';
        return params;
      case 'rejectedTaskCountVerifier':
        params.verifyStatus = 'rejected';
        return params;
      case 'notStartedTaskCountVerifier':
        params.verifyStatus = 'notStarted';
        return params;
      case 'completedAssignmentCount':
        params.status = 'complete';
        return params;
      case 'inProgressAssignmentCount':
        params.status = 'started';
        return params;
      case 'notStartedAssignmentCount':
        params.status = 'notStarted';
        return params;
      default:
        return params;
    }
  };

  handleShowTaskModal = (state = false): void => {
    this.setState({ showTaskModal: state });
  };

  onChangeView = (view: string): void => {
    this.setState({ chartView: view });
  };

  copyConfToClipboard = () => {
    // const company = this.props.company;
    const tasks = this.props.tasks;
    const templateName = tasks[this.props.templateId!].taskName;
    const partName = this.state.assignee
      ? tasks[this.props.templateId!].assigneeTitle
      : tasks[this.props.templateId!].templateReportOptions![
          this.state.chartView === 'verify'
            ? 'verifyTitle'
            : this.state.chartView + 'ProgressTitle'
        ];
    const conf = {
      companyId: this.props.companyId,
      timeLength: this.state.timeLength,
      projectId: this.props.activeProjectId,
      parentId: this.props.parentId,
      templateId: this.props.templateId,
      assigneeId: this.state.assignee?.value || undefined,
      splitMonths: this.state.splitMonths,
      splitField: this.state.split.value,
      fieldMap: this.props.fieldValues,
      order: 10,
      dashboardWidth: 6,
      dashboardHeight: 4,
      name: this.copyTextRef.current?.value ?? '',
      visiblePart: this.state.assignee ? 'assignee' : this.state.chartView,
      showRelative: this.state.showRelative,
      templateName: templateName,
      partName,
    };
    setClipboard(JSON.stringify(conf));
    notify(
      i18n.t('copied', { ns: 'taskManager' }),
      'successful',
      false,
      1000000
    );
  };

  makeButtonGroupsItems = () => {
    const templateReportOptions =
      this.props.tasks[this.props.templateId!].templateReportOptions;
    const items: any = {};
    if (templateReportOptions) {
      if (
        templateReportOptions.completedTaskCountMain ||
        templateReportOptions.inProgressTaskCountMain ||
        templateReportOptions.notStartedTaskCountMain ||
        templateReportOptions.averageProgressMain
      ) {
        items.main =
          templateReportOptions.mainProgressTitle ||
          i18n.t('mainTitle', { ns: 'taskManager' });
      }
      if (
        templateReportOptions.completedTaskCountSecondary ||
        templateReportOptions.inProgressTaskCountSecondary ||
        templateReportOptions.notStartedTaskCountSecondary ||
        templateReportOptions.averageProgressSecondary
      ) {
        items.secondary =
          templateReportOptions.secondaryProgressTitle ||
          i18n.t('secondaryTitle', { ns: 'taskManager' });
      }
      if (
        templateReportOptions.completedTaskCountVerifier ||
        templateReportOptions.rejectedTaskCountVerifier ||
        templateReportOptions.notStartedTaskCountVerifier
      ) {
        items.verify =
          templateReportOptions.verifyTitle ||
          i18n.t('verifyTitle', { ns: 'taskManager' });
      }
    }
    return items;
  };

  handleChangeDate = (
    splitMonths: number,
    startDate: number,
    toDate: number,
    timeLength: number
  ) => {
    this.setState({ splitMonths, startDate, toDate, timeLength });
  };

  handleChangeSelectedProject = (selectedProject: IOption[]) => {
    const projectId =
      selectedProject.length > 0 ? selectedProject[0].value : undefined;
    this.props.handleChangeProjectId(Number(projectId));
  };

  handleChangeAssigneeId = (selectedAssignee: IOption[]) => {
    const assigneeId = selectedAssignee[0];
    this.setState({
      assignee: assigneeId,
      chartView: assigneeId ? 'assignee' : 'main',
    });
  };
  handleChangSplit = (selected: IOption[]) => {
    const split = selected[0];
    this.setState({
      split,
    });
  };

  render() {
    if (this.state.loading) {
      return (
        <div className="progress">
          <div className="indeterminate" />
        </div>
      );
    }
    const {
      users,
      tasks,
      company,
      companyId,
      templateId,
      compactView,
      activeProjectId,
      fieldValues,
      inSurvey,
      isAdmin,
    } = this.props;

    const local = (company.local || getCalendarLocal()) as ILocal;

    if (!templateId) {
      return (
        <Message>
          {i18n.t('toPrepareTheReportYouShouldSelectAtTemplate', {
            ns: 'taskManager',
          })}
        </Message>
      );
    }
    const {
      toDate,
      chartData,
      chartView,
      startDate,
      tableData,
      assignee,
      split,
      splitMonths,
      selectedItem,
      showTaskModal,
      preparingReport,
    } = this.state;

    const tasksFieldValue = (fieldValues && { ...fieldValues }) || {};
    if (
      selectedItem &&
      selectedItem.searchParams &&
      selectedItem.searchParams.extraFieldKey &&
      selectedItem.searchParams.extraFieldKey !== '__project'
    ) {
      tasksFieldValue[selectedItem.searchParams.extraFieldKey] =
        selectedItem.searchParams.extraFieldValue;
    }
    const templateReportOptions = tasks[templateId].templateReportOptions!;
    const assigneeId = assignee ? (assignee.value as number) : null;

    const boxCount =
      chartView === 'main'
        ? (tableData.completedTaskCountMain ? 1 : 0) +
          (tableData.inProgressTaskCountMain ? 1 : 0) +
          (tableData.notStartedTaskCountMain ? 1 : 0) +
          (tableData.averageProgressMain ? 1 : 0)
        : chartView === 'secondary'
          ? (tableData.completedTaskCountSecondary ? 1 : 0) +
            (tableData.inProgressTaskCountSecondary ? 1 : 0) +
            (tableData.notStartedTaskCountSecondary ? 1 : 0) +
            (tableData.averageProgressSecondary ? 1 : 0)
          : chartView === 'verifier'
            ? (tableData.completedTaskCountVerifier ? 1 : 0) +
              (tableData.rejectedTaskCountVerifier ? 1 : 0) +
              (tableData.notStartedTaskCountVerifier ? 1 : 0)
            : (tableData.completedAssignmentCount ? 1 : 0) +
              (tableData.inProgressAssignmentCount ? 1 : 0) +
              (tableData.notStartedAssignmentCount ? 1 : 0) +
              (tableData.averageAssignmentProgress ? 1 : 0);

    const parentTemplates = new Map<number, string>();
    getParents(company, templateId, parentTemplates, tasks);
    const parentOptions: IOption[] = [];
    parentTemplates.forEach((v, k) => {
      parentOptions.push({
        label: v,
        // value: '__parent',
        value: `__ancestor/${k}`,
      });
    });
    const printUrl =
      `/t/${companyId}/print/tasks_report?startDate=${startDate}&toDate=${toDate}&projectId=${
        activeProjectId ? activeProjectId : undefined
      }&parentId=${this.props.parentId || undefined}&templateId=${
        this.props.templateId
      }&assigneeId=${assigneeId}&splitMonths=${splitMonths}&chartView=${chartView}&splitField=${
        split.value
      }&showRelative=${this.state.showRelative}` +
      Object.entries(fieldValues).reduce((result, item) => {
        return (
          result +
          '&' +
          encodeURIComponent(item[0]) +
          '=' +
          encodeURIComponent(item[1])
        );
      }, '');

    const fullFormat =
      getLanguageBaseOnTZ() === 'fa' ? 'jYYYY/jMM/jD' : 'YYYY/MM/D';

    const handleReportData = () => {
      let data = [];

      const chartDataOptions = chartData.reduce((acc: string[], data) => {
        Object.keys(data).map((el) => {
          if (!acc.includes(el)) {
            acc.push(el);
          }
        });
        return acc;
      }, []);
      const headerList = chartData.reduce(
        (acc: { [key: string]: any }, item, ind) => {
          acc[`col-${ind}`] = item.title;
          return acc;
        },
        []
      );

      const rowOptionListTitle: string[] = Object.values(templateReportOptions)
        .filter((option) => {
          return (
            chartDataOptions.includes(option) ||
            chartDataOptions.includes(
              `${option} (${i18n.t('percentage', {
                ns: 'taskManager',
              })})`
            )
          );
        })
        .reduce((acc: string[], option) => {
          if (!acc.includes(option)) acc.push(option);
          return acc;
        }, []);

      data.push({
        rowTitle: '',
        ...headerList,
      });

      const listOfRows = rowOptionListTitle.map((rowOption) => {
        const ret = {
          rowTitle: rowOption,
          ...chartData.reduce((acc: { [key: string]: any }, item, ind) => {
            acc[`col-${ind}`] =
              item?.[rowOption.trim()] ||
              item?.[
                `${rowOption.trim()} (${i18n.t('percentage', {
                  ns: 'taskManager',
                })})`
              ] ||
              0;
            return acc;
          }, []),
        };
        if (chartData[0][rowOption] === undefined) {
          ret.rowTitle = `${rowOption.trim()} (${i18n.t('percentage', {
            ns: 'taskManager',
          })})`;
        }
        return ret;
      });

      data = [...data, ...listOfRows];

      return data;
    };

    return (
      <ReportSection compactView={inSurvey}>
        {!this.props.readonly && (
          <ReportConfigContainer compactView={compactView}>
            <DateSelector
              showBaseDate={!inSurvey}
              compactView={true}
              onChangeDate={this.handleChangeDate}
              local={local as ILocal}
            />
            <div
              style={{
                width: '15rem',
                alignSelf: 'center',
              }}
            >
              <ReportRangeDate>{`${messages.getReportRangeDate(
                convertDigitToProperFormat(
                  (local === 'fa_IR@calendar=persian'
                    ? moment(startDate)
                    : moment(startDate).locale('en')
                  ).format(fullFormat)
                ),
                convertDigitToProperFormat(
                  (local === 'fa_IR@calendar=persian'
                    ? moment(toDate)
                    : moment(toDate).locale('en')
                  ).format(fullFormat)
                )
              )}`}</ReportRangeDate>

              {templateId && (
                <div className="input-field">
                  <Select
                    onChangeValues={this.handleChangSplit}
                    label={i18n.t('split', { ns: 'taskManager' }) as string}
                    placeholder={
                      i18n.t('split', { ns: 'taskManager' }) as string
                    }
                    values={[split]}
                    allowRemove={false}
                    options={tasks[templateId].fields
                      .reduce(
                        (current: IOption[], item: any) => {
                          if (!current[current.length - 1]) {
                            current.splice(current.length - 1, 1);
                          }
                          if (
                            this.hasOptions(item, activeProjectId, templateId)
                          )
                            current.push({
                              label: item.title,
                              value: item.name,
                            });
                          return current;
                        },
                        [
                          {
                            label: i18n.t('time', {
                              ns: 'taskManager',
                            }) as string,
                            value: '__time',
                          },
                          activeProjectId
                            ? (null as any)
                            : {
                                label: i18n.t('project', {
                                  ns: 'taskManager',
                                }) as string,
                                value: '__project',
                              },
                          ...parentOptions,
                        ] as IOption[]
                      )
                      .filter((e: any) => !!e)}
                  />
                </div>
              )}
              {(templateReportOptions.completedAssignmentCount ||
                templateReportOptions.inProgressAssignmentCount ||
                templateReportOptions.notStartedAssignmentCount ||
                templateReportOptions.averageAssignmentProgress) &&
                split.value === '__time' &&
                Object.values(fieldValues).filter((it) => !!it).length ===
                  0 && (
                  <div className="input-field col s6 m3">
                    <Select
                      onChangeValues={this.handleChangeAssigneeId}
                      label={messages.assignee(
                        tasks[templateId].assigneeTitle!
                      )}
                      placeholder={i18n.t('select', { ns: 'taskManager' })}
                      values={assignee ? [assignee] : undefined}
                      options={Object.values(company.members).reduce(
                        (temp, member) => {
                          const user = users[member.userId];
                          temp.push({
                            value: user.id,
                            label: user.name,
                          });
                          return temp;
                        },
                        [] as Array<{ value: number; label: string }>
                      )}
                    />
                  </div>
                )}
              <p className="switch" style={{ color: black }}>
                <label>
                  <input
                    name="archived"
                    id="archived"
                    type="checkbox"
                    checked={this.state.showRelative}
                    onChange={() =>
                      this.setState({
                        showRelative: !this.state.showRelative,
                      })
                    }
                  />
                  <span
                    className="lever"
                    style={{ marginInlineStart: '0', marginInlineEnd: '10px' }}
                  />
                </label>
                {i18n.t('showRelative', { ns: 'taskManager' }) as string}
              </p>
              <Button
                style={{ marginTop: '0.5rem' }}
                color="blue"
                onClick={this.onPrepareReport}
              >
                <Icon className="right" type="trending-up" />
                <span>
                  {
                    i18n.t('prepareReport', {
                      ns: 'taskManager',
                    }) as string
                  }
                </span>
              </Button>
            </div>
          </ReportConfigContainer>
        )}
        <div
          style={{
            margin: inSurvey ? '1rem 0rem' : '1rem',
            flex: '1',
            overflow: 'auto',
          }}
        >
          {preparingReport ? (
            <div className="progress">
              <div className="indeterminate" />
            </div>
          ) : (
            this.state.chartData.length > 0 &&
            Object.keys(tableData).length > 2 && (
              <React.Fragment>
                <ReportHeader>
                  {!assigneeId && (
                    <ButtonGroup
                      items={this.makeButtonGroupsItems()}
                      selected={chartView}
                      onSelect={this.onChangeView}
                    />
                  )}
                  <ExportExcell
                    getData={handleReportData}
                    exportFileName="report"
                    body={
                      <Button
                        style={{ marginInlineStart: '1rem' }}
                        color="blue-outline"
                      >
                        <Icon type={'download'} />
                      </Button>
                    }
                  />
                  <Button
                    style={{ marginInlineStart: '1rem' }}
                    size="small"
                    color="blue-outline"
                    onClick={() => {
                      window.open(printUrl, '_blank');
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon className="right" type="printer" />
                    <span>
                      {
                        i18n.t('printReport', {
                          ns: 'taskManager',
                        }) as string
                      }
                    </span>
                  </Button>
                  {isAdmin && !this.props.readonly && (
                    <Button
                      style={{ marginInlineStart: '1rem' }}
                      size="small"
                      color="blue-outline"
                      onClick={this.copyConfToClipboard}
                    >
                      <Icon className="right" type="copy" />
                      <span>
                        {
                          i18n.t('copy', {
                            ns: 'taskManager',
                          }) as string
                        }
                      </span>
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="blue-outline"
                    style={{ marginInlineStart: '1rem' }}
                    onClick={this.handleClickOnShowAll(chartData)}
                  >
                    {
                      i18n.t('showAllTasksInThisPeriodOfTime', {
                        ns: 'taskManager',
                      }) as string
                    }
                  </Button>
                </ReportHeader>
                <ReportBody>
                  {!assigneeId ? (
                    <React.Fragment>
                      {chartView && (
                        <React.Fragment>
                          <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <div
                              style={{
                                paddingInlineEnd: 0,
                                paddingInlineStart: 0,
                              }}
                            >
                              <ReportInfoBoxes
                                type={chartView as any}
                                templateReportOptions={templateReportOptions}
                                tableData={tableData}
                                horizontal={true}
                              />
                            </div>
                            <div
                              className="col s12 m12 l12"
                              style={{
                                paddingInlineEnd: 0,
                                paddingInlineStart: 0,
                                overflowX: 'auto',
                              }}
                              ref={this.chartContainerRef}
                            >
                              <ReportChart
                                height={
                                  this.props.height
                                    ? Math.min(
                                        this.props.height -
                                          (window.innerWidth < 600
                                            ? boxCount * 105
                                            : 0),
                                        500
                                      )
                                    : undefined
                                }
                                width={
                                  chartData.length * 30 >
                                  (this.chartContainerRef.current
                                    ?.clientWidth || 0)
                                    ? `${chartData.length * 30}px`
                                    : '100%'
                                }
                                type={chartView as any}
                                chartData={chartData}
                                templateReportOptions={templateReportOptions}
                                onClickOnBar={this.handleClickOnBar}
                                showRelative={this.state.reportShowRelative}
                              />
                            </div>
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div className="row">
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <div
                            style={{
                              paddingInlineEnd: 0,
                              paddingInlineStart: 0,
                            }}
                          >
                            <ReportInfoBoxes
                              type="assignee"
                              templateReportOptions={templateReportOptions}
                              tableData={tableData}
                              horizontal={true}
                            />
                          </div>
                          <div
                            className="col s12 m12"
                            style={{
                              paddingInlineEnd: '0',
                              paddingInlineStart: '0',
                              width: '100%',
                            }}
                          >
                            <ActionLink
                              onClick={this.handleClickOnShowAll(chartData)}
                              style={{ marginTop: '1rem' }}
                            >
                              {
                                i18n.t('showAllTasksInThisPeriodOfTime', {
                                  ns: 'taskManager',
                                }) as string
                              }
                            </ActionLink>
                            <ReportChart
                              height={
                                this.props.height
                                  ? Math.min(
                                      this.props.height -
                                        (window.innerWidth < 600
                                          ? boxCount * 105
                                          : 0),
                                      500
                                    )
                                  : undefined
                              }
                              type="assignee"
                              chartData={chartData}
                              templateReportOptions={templateReportOptions}
                              onClickOnBar={this.handleClickOnBar}
                              hasTooltip={true}
                              showRelative={this.state.reportShowRelative}
                            />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </ReportBody>
              </React.Fragment>
            )
          )}
        </div>
        {selectedItem && showTaskModal && (
          <TaskReportModal
            assigneeId={assigneeId ? assigneeId : undefined}
            companyId={companyId}
            endDate={monthToTimestamp(selectedItem.endMonthIndex, local)}
            onClose={this.handleShowTaskModal}
            parentId={selectedItem.searchParams.parentId || this.props.parentId}
            ancestorId={selectedItem.searchParams.ancestorId}
            projectId={
              this.props.activeProjectId ||
              (selectedItem.searchParams && selectedItem.searchParams.projectId)
            }
            searchParams={selectedItem.searchParams}
            startDate={monthToTimestamp(selectedItem.startMonthIndex, local)}
            templateId={this.props.templateId}
            fieldValues={tasksFieldValue}
            local={local}
          />
        )}
      </ReportSection>
    );
  }

  private hasOptions(
    item: any,
    activeProjectId: number | undefined,
    templateId: number
  ) {
    return (
      (item.options && Object.values(item.options).length > 0) ||
      Object.values(this.props.projects || {}).find((project) => {
        return (
          (((!activeProjectId || project.id === activeProjectId) &&
            project.templates?.find((item) => item.taskId === templateId)
              ?.overrides?.fieldOptions?.[item.name!]?.length) ??
            0) > 0 || false
        );
      })
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const users = state.users.map;
  const thisUser = state.thisUser;
  const tasks = state.tasks.data;
  const company = state.companies.data[props.companyId];
  const projects = company.projects;
  const isAdmin = isCompanyAdmin(thisUser, company);
  return { users, tasks, company, projects, thisUser, isAdmin };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireUsers: (usersIds: number[]) =>
      appDispatch(requireSeveralUser({ usersList: usersIds })),
    getReport: (
      companyId: number,
      templateReport: ITemplateReportMark,
      startDate: number,
      toDate: number,
      projectId: NumberOrNull,
      parentId: NumberOrNull,
      templateId: number,
      assigneeId: NumberOrNull,
      splitMonths: number,
      splitField: StringOrNull,
      fieldValues: { [key: string]: string }
    ) =>
      appDispatch(
        getTaskReport({
          companyId,
          templateReport,
          startDate,
          toDate,
          projectId,
          parentId,
          templateId,
          assigneeId,
          splitMonths,
          splitField,
          fieldValues,
        })
      ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskReport);
